/**
 * DICOM Overlay Plane (group 60xx) support for the Basic (Cornerstone) viewer.
 *
 * Some scanners ship burned-in graphics — Siemens AutoAlign spine level lines
 * and labels being the case that prompted this — not as pixels and not as a
 * GSPS/PR object, but as an Overlay Plane: a 1-bit-per-pixel bitmap in a
 * repeating group 6000, 6002 … 601e:
 *
 *   (60xx,0010) US  Overlay Rows
 *   (60xx,0011) US  Overlay Columns
 *   (60xx,0015) IS  Number of Frames in Overlay
 *   (60xx,0022) LO  Overlay Description
 *   (60xx,0040) CS  Overlay Type            G = graphics, R = ROI
 *   (60xx,0050) SS  Overlay Origin          row\column, 1-based
 *   (60xx,0051) US  Image Frame Origin      1-based
 *   (60xx,0100) US  Overlay Bits Allocated  1 for a standalone plane
 *   (60xx,0102) US  Overlay Bit Position    0 for a standalone plane
 *   (60xx,1500) LO  Overlay Label
 *   (60xx,3000) OW  Overlay Data            packed bits, LSB of byte 0 first
 *
 * MedDream (the Advance Viewer) composites these; Cornerstone3D has no overlay
 * renderer at all, which is why such markings are visible there and not here.
 * This module does the extraction + rasterisation half; DicomOverlayLayer does
 * the drawing.
 *
 * Cornerstone's own `overlayPlaneModule` metadata provider is deliberately NOT
 * used:
 *   - the wadors implementation reads `metaData.Value[dataOffset + i]`, which
 *     is not where DICOMweb JSON puts binary values, so it yields nothing;
 *   - the wadouri implementation unpacks into a plain JS Array (262 144
 *     elements for a 512x512 plane) and ignores multi-frame overlays.
 * Reading the tags here keeps both schemes on one code path and lets the
 * drawing side work off a packed Uint8Array.
 */

import dicomImageLoader from "@cornerstonejs/dicom-image-loader";

// Colour graphic overlays are drawn in. DICOM displays a graphic overlay at
// maximum intensity, which on the monochrome images that carry them means
// white — the same as MedDream. Change this one constant to tint them.
export const DICOM_OVERLAY_COLOR = "#FFFFFF";

// Repeating overlay groups: 6000, 6002, … 601e (odd groups are not overlays).
const OVERLAY_GROUPS = [];
for (let group = 0x6000; group <= 0x601e; group += 2) {
  OVERLAY_GROUPS.push(group);
}

// Extraction is keyed by imageId and caches the negative result too, because
// the draw path runs on every IMAGE_RENDERED — i.e. on every pan/zoom frame —
// and must not re-walk the dataset each time.
// Bounded because each entry holds a copy of the overlay bits — 32 KB for a
// 512x512 plane, so ~10 MB at the limit.
const planeCache = new Map();
const PLANE_CACHE_LIMIT = 300;

// Rasterised planes, keyed by imageId + group + overlay frame + colour. Kept
// small deliberately: a 512x512 RGBA canvas is 1 MB, and re-rasterising costs
// only a couple of milliseconds, so this only needs to cover the current image
// and its immediate neighbours during a scroll.
const bitmapCache = new Map();
const BITMAP_CACHE_LIMIT = 12;

// One warning per (imageId, group) for overlays we can see but cannot read.
const warnedKeys = new Set();

const warnOnce = (key, ...args) => {
  if (warnedKeys.has(key)) return;
  warnedKeys.add(key);
  console.warn(...args);
};

const putBounded = (cache, key, value, limit) => {
  cache.set(key, value);
  if (cache.size > limit) {
    // Insertion-ordered: drop the oldest entry.
    cache.delete(cache.keys().next().value);
  }
};

// ── Tag helpers ──────────────────────────────────────────────────────────────

const hex4 = (value) => value.toString(16).padStart(4, "0");

/** dicom-parser tag string, e.g. 0x6000,0x3000 → "x60003000". */
const dpTag = (group, element) => `x${hex4(group)}${hex4(element)}`;

/** DICOMweb JSON key, e.g. 0x6000,0x3000 → "60003000". */
const jsonTag = (group, element) => `${hex4(group)}${hex4(element)}`.toUpperCase();

const toInt = (value, fallback) => {
  const parsed = typeof value === "number" ? value : parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
};

/** Decode a DICOMweb InlineBinary (base64) value into bytes. */
const decodeInlineBinary = (base64) => {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

// ── Per-scheme extraction ────────────────────────────────────────────────────

/**
 * Shared validation + normalisation. `read` is a per-scheme accessor for the
 * raw tag values; returns a plane descriptor, or null when the group is absent
 * or unusable.
 */
const buildPlane = (group, read, warnKey) => {
  const rows = toInt(read.number(group, 0x0010), 0);
  const columns = toInt(read.number(group, 0x0011), 0);
  if (rows <= 0 || columns <= 0) return null;

  const bitsAllocated = toInt(read.number(group, 0x0100), 1);
  const bitPosition = toInt(read.number(group, 0x0102), 0);

  const bytes = read.data(group);
  if (!bytes || bytes.length === 0) {
    // No (60xx,3000): a retired "embedded" overlay living in the unused high
    // bits of the image pixel data. Reconstructing it means re-reading the
    // decoded frame through a bit mask, which this module does not do.
    if (bitsAllocated > 1) {
      warnOnce(
        warnKey,
        `[DicomOverlay] Group ${hex4(group)} is an embedded overlay ` +
          `(OverlayBitsAllocated=${bitsAllocated}, BitPosition=${bitPosition}); ` +
          "embedded overlays are not rendered."
      );
    }
    return null;
  }

  const bitsPerFrame = rows * columns;
  const availableBits = bytes.length * 8;
  if (availableBits < bitsPerFrame) {
    warnOnce(
      warnKey,
      `[DicomOverlay] Group ${hex4(group)} declares ${rows}x${columns} but ` +
        `carries only ${availableBits} bits; ignoring.`
    );
    return null;
  }

  const declaredFrames = Math.max(1, toInt(read.number(group, 0x0015), 1));

  // (60xx,0050) is row\column and 1-based; x/y here are 0-based image pixels.
  const originRow = toInt(read.number(group, 0x0050, 0), 1);
  const originColumn = toInt(read.number(group, 0x0050, 1), 1);

  return {
    group,
    rows,
    columns,
    // Offset of the overlay's top-left corner within the image, in image pixels.
    x: originColumn - 1,
    y: originRow - 1,
    type: read.string(group, 0x0040) || "G",
    label: read.string(group, 0x1500) || "",
    description: read.string(group, 0x0022) || "",
    // Never trust the declared count past what the data can actually hold.
    frames: Math.min(declaredFrames, Math.floor(availableBits / bitsPerFrame)),
    // 1-based index of the image frame the overlay's first frame applies to.
    imageFrameOrigin: Math.max(1, toInt(read.number(group, 0x0051), 1)),
    bits: bytes,
  };
};

/** Overlay planes from a dicom-parser dataSet (wadouri: / dicomweb: / dicomfile:). */
const extractFromDataSet = (dataSet, imageId) => {
  const planes = [];
  for (const group of OVERLAY_GROUPS) {
    const read = {
      number: (g, element, index = 0) => {
        const tag = dpTag(g, element);
        if (!dataSet.elements[tag]) return undefined;
        // (60xx,0050) is SS, (60xx,0015) is IS, the rest of what we read is US.
        if (element === 0x0050) return dataSet.int16(tag, index);
        if (element === 0x0015) return parseInt(dataSet.string(tag), 10);
        return dataSet.uint16(tag, index);
      },
      string: (g, element) => dataSet.string(dpTag(g, element)),
      data: (g) => {
        const el = dataSet.elements[dpTag(g, 0x3000)];
        if (!el || !el.length) return null;
        // A copy, not a view: a view would pin the whole parsed .dcm buffer in
        // memory for as long as this module caches the plane, long after the
        // loader has dropped the dataset.
        return dataSet.byteArray.slice(el.dataOffset, el.dataOffset + el.length);
      },
    };
    const plane = buildPlane(group, read, `${imageId}|${group}`);
    if (plane) planes.push(plane);
  }
  return planes;
};

/** Overlay planes from DICOMweb instance metadata JSON (wadors:). */
const extractFromWadoRsMetadata = (metadata, imageId) => {
  const planes = [];
  for (const group of OVERLAY_GROUPS) {
    const read = {
      number: (g, element, index = 0) => {
        const el = metadata[jsonTag(g, element)];
        return Array.isArray(el?.Value) ? el.Value[index] : undefined;
      },
      string: (g, element) => {
        const el = metadata[jsonTag(g, element)];
        return Array.isArray(el?.Value) ? el.Value[0] : undefined;
      },
      data: (g) => {
        const el = metadata[jsonTag(g, 0x3000)];
        if (!el) return null;
        if (el.InlineBinary) {
          try {
            return decodeInlineBinary(el.InlineBinary);
          } catch (err) {
            warnOnce(
              `${imageId}|${g}|inline`,
              `[DicomOverlay] Could not decode InlineBinary for group ${hex4(g)}:`,
              err
            );
            return null;
          }
        }
        if (el.BulkDataURI) {
          // The bytes sit behind a second, separately-authenticated request.
          // Fetching them would make extraction async on the render path, so
          // the overlay is reported as unreadable instead.
          warnOnce(
            `${imageId}|${g}|bulk`,
            `[DicomOverlay] Group ${hex4(g)} overlay data is served as ` +
              "BulkDataURI, which is not fetched; overlay not rendered."
          );
        }
        return null;
      },
    };
    const plane = buildPlane(group, read, `${imageId}|${group}`);
    if (plane) planes.push(plane);
  }
  return planes;
};

// ── Public API ───────────────────────────────────────────────────────────────

/**
 * Overlay planes carried by `imageId`, or an empty array. Cheap to call
 * repeatedly — results (including "none") are cached per imageId.
 */
export const getOverlayPlanes = (imageId) => {
  if (!imageId || typeof imageId !== "string") return [];
  if (planeCache.has(imageId)) return planeCache.get(imageId);

  let planes = [];
  try {
    if (imageId.startsWith("wadors:")) {
      const metadata = dicomImageLoader?.wadors?.metaDataManager?.get(imageId);
      // Absent until /metadata has been registered — not the same as "none".
      if (!metadata) return [];
      planes = extractFromWadoRsMetadata(metadata, imageId);
    } else {
      const { url } = dicomImageLoader.wadouri.parseImageId(imageId);
      const dataSet = dicomImageLoader.wadouri.dataSetCacheManager.get(url);
      // Absent until the image has loaded — do not cache that as "no overlay".
      if (!dataSet?.elements) return [];
      planes = extractFromDataSet(dataSet, imageId);
    }
  } catch (err) {
    warnOnce(
      `${imageId}|extract`,
      "[DicomOverlay] Overlay extraction failed for",
      imageId,
      err
    );
    planes = [];
  }

  putBounded(planeCache, imageId, planes, PLANE_CACHE_LIMIT);
  return planes;
};

/**
 * 0-based frame number an imageId addresses within its instance. Both schemes
 * spell the frame 1-based: `wadouri:…?frame=N` and `wadors:…/frames/N`. A
 * single-frame instance has no frame in its imageId and is frame 0.
 */
export const getImageFrameIndex = (imageId) => {
  if (!imageId || typeof imageId !== "string") return 0;
  const match = imageId.startsWith("wadors:")
    ? imageId.match(/\/frames\/(\d+)/)
    : imageId.match(/[?&]frame=(\d+)/);
  const frame = match ? parseInt(match[1], 10) : NaN;
  return Number.isFinite(frame) && frame > 0 ? frame - 1 : 0;
};

/**
 * 0-based frame of `plane` that applies to 0-based image frame
 * `imageFrameIndex`, or -1 when the overlay does not cover that frame.
 */
export const getOverlayFrameIndex = (plane, imageFrameIndex) => {
  if (plane.frames <= 1) return 0;
  const index = imageFrameIndex - (plane.imageFrameOrigin - 1);
  return index >= 0 && index < plane.frames ? index : -1;
};

/**
 * Rasterise one overlay frame into an RGBA canvas of the plane's own
 * dimensions: set bits in `color`, unset bits transparent. Cached.
 */
export const getOverlayBitmap = (
  imageId,
  plane,
  overlayFrameIndex,
  color = DICOM_OVERLAY_COLOR
) => {
  const key = `${imageId}|${plane.group}|${overlayFrameIndex}|${color}`;
  if (bitmapCache.has(key)) return bitmapCache.get(key);

  const { rows, columns, bits } = plane;
  const canvas = document.createElement("canvas");
  canvas.width = columns;
  canvas.height = rows;
  const ctx = canvas.getContext("2d");
  const imageData = ctx.createImageData(columns, rows);
  const rgba = imageData.data;

  const r = parseInt(color.slice(1, 3), 16);
  const g = parseInt(color.slice(3, 5), 16);
  const b = parseInt(color.slice(5, 7), 16);

  // Overlay frames are packed continuously — no byte padding between them.
  const firstBit = overlayFrameIndex * rows * columns;
  const pixelCount = rows * columns;
  for (let i = 0; i < pixelCount; i++) {
    const bit = firstBit + i;
    // Overlay data is packed low-bit-first within each byte.
    if ((bits[bit >> 3] >> (bit & 7)) & 1) {
      const o = i * 4;
      rgba[o] = r;
      rgba[o + 1] = g;
      rgba[o + 2] = b;
      rgba[o + 3] = 255;
    }
  }
  ctx.putImageData(imageData, 0, 0);

  putBounded(bitmapCache, key, canvas, BITMAP_CACHE_LIMIT);
  return canvas;
};

// ── Geometry ─────────────────────────────────────────────────────────────────

/**
 * World position of the image-pixel CORNER (px, py). A viewport's imageData
 * origin is the CENTRE of pixel (0,0), so corner (0,0) — the top-left of the
 * image — sits half a pixel back along both axes.
 *
 * This deliberately works off the viewport's own imageData rather than
 * Cornerstone's `imageToWorldCoords`, which pairs rowCosines with
 * rowPixelSpacing (i.e. the spacing of the other axis). That pairing cancels
 * out for square pixels but not otherwise, and imageData is what
 * `worldToCanvas` itself is derived from.
 */
export const imagePixelToWorld = (imageData, px, py) => {
  const { origin, direction, spacing } = imageData;
  const u = (px - 0.5) * spacing[0];
  const v = (py - 0.5) * spacing[1];
  return [
    origin[0] + direction[0] * u + direction[3] * v,
    origin[1] + direction[1] * u + direction[4] * v,
    origin[2] + direction[2] * u + direction[5] * v,
  ];
};

/**
 * Affine transform taking overlay-grid coordinates to viewport canvas
 * coordinates, as the six arguments of CanvasRenderingContext2D.setTransform.
 *
 * Derived from three corners of the overlay's own grid rather than from the
 * camera, so zoom, pan, rotation and flips all come along for free. Returns
 * null when the mapping is not usable (a degenerate camera, an off-plane
 * viewport).
 */
export const computeOverlayTransform = (imageData, plane, worldToCanvas) => {
  const p0 = worldToCanvas(imagePixelToWorld(imageData, plane.x, plane.y));
  const pu = worldToCanvas(
    imagePixelToWorld(imageData, plane.x + plane.columns, plane.y)
  );
  const pv = worldToCanvas(
    imagePixelToWorld(imageData, plane.x, plane.y + plane.rows)
  );
  if (!p0 || !pu || !pv) return null;

  const transform = {
    a: (pu[0] - p0[0]) / plane.columns,
    b: (pu[1] - p0[1]) / plane.columns,
    c: (pv[0] - p0[0]) / plane.rows,
    d: (pv[1] - p0[1]) / plane.rows,
    e: p0[0],
    f: p0[1],
  };
  const values = Object.values(transform);
  if (!values.every(Number.isFinite)) return null;
  // A collapsed mapping would paint nothing useful and can divide by zero
  // downstream.
  if (transform.a * transform.d - transform.b * transform.c === 0) return null;
  return transform;
};

/** Drop everything held, for a study change or viewer teardown. */
export const clearDicomOverlayCaches = () => {
  planeCache.clear();
  bitmapCache.clear();
  warnedKeys.clear();
};
